/**
 * Authentication Context Provider
 * -------------------------------
 *
 * Purpose:
 * This module provides authentication state management for the entire application
 * through React's Context API. It centralizes all authentication-related logic
 * and state, making it available to any component in the component tree.
 *
 * Responsibilities:
 * - Maintain authentication state (isAuthenticated, user data)
 * - Handle user login via GitHub OAuth flow
 * - Manage authentication tokens
 * - Provide logout functionality
 * - Refresh user data when needed
 * - Persist authentication between sessions using localStorage
 * - Validate tokens with the backend
 *
 * Usage:
 * 1. Wrap your application with the AuthProvider:
 *    <AuthProvider>
 *      <App />
 *    </AuthProvider>
 *
 * 2. Use the authentication state and functions in any component:
 *    const { isAuthenticated, user, login, logout } = useAuth();
 *
 * 3. Protect routes based on authentication status:
 *    if (!isAuthenticated) return <Navigate to="/" />;
 *
 * Authentication Flow:
 * 1. User initiates GitHub OAuth flow
 * 2. GitHub redirects back with a code
 * 3. The code is exchanged for a token via backend API
 * 4. User data is fetched and stored
 * 5. Authentication state is updated
 *
 * Security Considerations:
 * - Tokens are stored in localStorage (consider more secure alternatives for production)
 * - Token validation occurs on initial load
 * - API requests include Authorization headers
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase, type UserProfile } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';

/**
 * Authentication context interface that provides authentication state
 * and methods to components throughout the application
 */
interface AuthContextType {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is being checked */
  isLoading: boolean;
  /** Currently authenticated user data or null if not authenticated */
  user: User | null;
  /** User profile data from Supabase */
  profile: UserProfile | null;
  /** Supabase session */
  session: Session | null;
  /**
   * Initiates the login flow with GitHub OAuth
   * Redirects user to GitHub OAuth authorization
   */
  login: () => Promise<void>;
  /**
   * Logs the user out by invalidating their GitHub OAuth session
   * Redirects to login page when complete
   */
  logout: () => Promise<void>;
  /**
   * Refreshes the user data by checking the session
   */
  refreshProfile: () => Promise<void>;
  /**
   * Refreshes the user session and updates auth state
   * Retrieves the current session from Supabase and updates the authentication state
   */
  refreshSession: () => Promise<void>;
}

// Default context with empty implementations
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  profile: null,
  session: null,
  login: async () => {},
  refreshProfile: async () => {},
  logout: async () => {},
  refreshSession: async () => {},
});

/**
 * Custom hook to access authentication context from any component
 * @returns Authentication context with user state and auth methods
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component that manages user authentication state
 * and provides authentication methods to the entire application
 *
 * @param children - Child components that will have access to auth context
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Function to fetch user profile from database
  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        // console.error('Error fetching user profile:', error);
        return;
      }

      if (data) {
        setProfile(data as UserProfile);
      } else {
        // If profile doesn't exist, it might be a new user
        // The profile will be created by a database trigger or backend function
        // console.log('Profile not found, might be a new user');
      }
    } catch {
      // console.error('Failed to fetch user profile:', error);
    }
  }, [user]);

  // Initialize the auth state when the component mounts
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);

      // Get current session
      const { data: { session: currentSession } } = await supabase.auth.getSession();

      if (currentSession) {
        setSession(currentSession);
        setUser(currentSession.user);

        // Fetch user profile from our database
        // We can call refreshProfile directly here but need to ensure it's safe since state updates are async
        // However, since we just set user, we might want to pass the user ID explicitly if refreshProfile depends on state 'user'
        // But refreshProfile depends on 'user' state which won't be updated until next render.
        // Better implementation for init: fetch profile manually here using currentSession.user.
      }

      setIsLoading(false);

      // Listen for auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        // console.log('Auth state changed:', event);

        setSession(newSession);
        setUser(newSession?.user || null);

        if (event === 'SIGNED_IN' || (event === 'TOKEN_REFRESHED' && newSession)) {
           // We can trigger profile refresh, but notice refreshProfile depends on 'user' state.
           // To be safe, we should probably fetch using newSession.user.id directly if we refactored refreshProfile.
           // For now, let's rely on the effect dependency if we add it, OR better, let's make refreshProfile accept an optional user arg.
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
        }
      });

      // Cleanup subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    };

    initAuth();
  }, []); // We keep this empty to run once on mount

  // We add a separate effect to refresh profile when user changes
  useEffect(() => {
    if (user) {
        refreshProfile();
    }
  }, [user, refreshProfile]);


  // Login with GitHub
  const login = async (): Promise<void> => {
    try {
      // Save current location for redirect after auth
      const currentPath = window.location.pathname;
      if (currentPath !== '/') {
        localStorage.setItem('auth_redirect', currentPath);
      }

      // Open GitHub OAuth flow
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          // redirectTo: `http://localhost:5173/auth/callback`, for local development
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'read:user user:email'
        }
      });

      if (error) {
        console.error('Login error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to initiate login:', error);
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Refresh the session and user data
  const refreshSession = async (): Promise<void> => {
    try {
      // Get the current session from Supabase
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Error refreshing session:', error);
        throw error;
      }

      // Update auth state based on session
      setSession(session);
      setUser(session?.user || null);

      // If we have a session and user, also refresh the profile data
      if (session?.user) {
        // We rely on the useEffect [user] to refresh profile, or we can call it here if we await state update? No.
        // It's safer to just let the state update trigger the effect.
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      // On critical errors, clear the auth state
      setSession(null);
      setUser(null);
      setProfile(null);
    }
  };

  // Auth context value
  const value: AuthContextType = {
    isAuthenticated: !!session,
    isLoading,
    user,
    profile,
    session,
    login,
    logout,
    refreshProfile,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
